import { html } from 'lit-html';

export const Button = ({
  children,
  onClick,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  fullWidth = false,
  icon = null,
  iconPosition = 'left',
  type = 'button',
  className = ''
}) => {
  const buttonClasses = [
    'btn',
    `btn-${variant}`,
    `btn-${size}`,
    fullWidth ? 'btn-full-width' : '',
    loading ? 'btn-loading' : '',
    className
  ].filter(Boolean).join(' ');

  const buttonContent = html`
    ${loading ? html`<span class="btn-spinner"></span>` : ''}
    ${!loading && icon && iconPosition === 'left' ? icon : ''}
    ${!loading && children ? html`<span class="btn-text">${children}</span>` : ''}
    ${!loading && icon && iconPosition === 'right' ? icon : ''}
  `;

  return html`
    <button
      type=${type}
      class=${buttonClasses}
      ?disabled=${disabled || loading}
      @click=${(e) => { e.stopPropagation(); onClick && onClick(e); }}
    >
      ${buttonContent}
    </button>
    
    <style>
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px 24px;
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
        text-decoration: none;
        border-radius: 8px;
        border: none;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
        transform-style: preserve-3d;
        min-height: 44px;
      }

      /* Tamanhos */
      .btn-small {
        padding: 8px 16px;
        font-size: 13px;
        min-height: 36px;
      }

      .btn-large {
        padding: 16px 32px;
        font-size: 16px;
        min-height: 52px;
      }

      .btn-icon {
        padding: 10px;
        min-height: 40px;
        min-width: 40px;
        gap: 0;
      }

      .btn-icon svg {
        width: 20px;
        height: 20px;
      }

      /* Largura total */
      .btn-full-width {
        width: 100%;
      }

      /* Variante Primary - Azul Sólido com Neon Blur */
      .btn-primary {
        background: var(--primary-color, #1877F2);
        color: #ffffff !important;
        border: none;
        box-shadow:
          0 0 8px color-mix(in srgb, var(--primary-color, #1877F2) 50%, transparent),
          0 0 16px color-mix(in srgb, var(--primary-color, #1877F2) 30%, transparent),
          0 0 24px color-mix(in srgb, var(--primary-color, #1877F2) 15%, transparent);
        position: relative;
        overflow: hidden;
      }

      /* Noise texture overlay */
      .btn-primary::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: inherit;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
        opacity: 0.06;
        pointer-events: none;
        z-index: 1;
        mix-blend-mode: overlay;
      }

      .btn-primary .btn-text,
      .btn-primary svg {
        position: relative;
        z-index: 2;
        color: #ffffff !important;
      }

      .btn-primary:hover:not(:disabled) {
        background: var(--primary-color, #1877F2);
        transform: translateY(-3px);
        box-shadow:
          0 0 12px color-mix(in srgb, var(--primary-color, #1877F2) 70%, transparent),
          0 0 24px color-mix(in srgb, var(--primary-color, #1877F2) 50%, transparent),
          0 0 40px color-mix(in srgb, var(--primary-color, #1877F2) 30%, transparent),
          0 0 60px color-mix(in srgb, var(--primary-color, #1877F2) 15%, transparent);
        animation: neon-pulse-btn 1.5s ease-in-out infinite;
      }

      .btn-primary:active:not(:disabled) {
        transform: translateY(0);
        box-shadow:
          0 0 8px color-mix(in srgb, var(--primary-color, #1877F2) 60%, transparent),
          0 0 16px color-mix(in srgb, var(--primary-color, #1877F2) 40%, transparent);
        animation: none;
      }

      @keyframes neon-pulse-btn {
        0%, 100% {
          box-shadow:
            0 0 12px color-mix(in srgb, var(--primary-color, #1877F2) 70%, transparent),
            0 0 24px color-mix(in srgb, var(--primary-color, #1877F2) 50%, transparent),
            0 0 40px color-mix(in srgb, var(--primary-color, #1877F2) 30%, transparent);
        }
        50% {
          box-shadow:
            0 0 16px color-mix(in srgb, var(--primary-color, #1877F2) 80%, transparent),
            0 0 32px color-mix(in srgb, var(--primary-color, #1877F2) 60%, transparent),
            0 0 50px color-mix(in srgb, var(--primary-color, #1877F2) 35%, transparent);
        }
      }

      /* Variante Secondary - Borda Azul com Hover Neon */
      .btn-secondary {
        background: transparent;
        color: var(--primary-color, #1877F2);
        position: relative;
        border: 2px solid var(--primary-color, #1877F2);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        box-shadow:
          0 0 4px color-mix(in srgb, var(--primary-color, #1877F2) 30%, transparent),
          0 0 8px color-mix(in srgb, var(--primary-color, #1877F2) 15%, transparent);
      }

      .btn-secondary:hover:not(:disabled) {
        color: #ffffff !important;
        background: var(--primary-color, #1877F2);
        border-color: var(--primary-color, #1877F2);
        transform: translateY(-3px);
        box-shadow:
          0 0 12px color-mix(in srgb, var(--primary-color, #1877F2) 70%, transparent),
          0 0 24px color-mix(in srgb, var(--primary-color, #1877F2) 50%, transparent),
          0 0 40px color-mix(in srgb, var(--primary-color, #1877F2) 30%, transparent);
      }

      .btn-secondary:hover:not(:disabled) .btn-text,
      .btn-secondary:hover:not(:disabled) svg {
        color: #ffffff !important;
      }

      /* Variante Success */
      .btn-success {
        background: linear-gradient(135deg, var(--secondary-color) 0%, var(--success) 100%);
        color: white;
        box-shadow: 0 4px 12px color-mix(in srgb, var(--secondary-color) 25%, transparent);
      }

      .btn-success:hover:not(:disabled) {
        background: linear-gradient(135deg, color-mix(in srgb, var(--secondary-color) 90%, black) 0%, color-mix(in srgb, var(--success) 90%, black) 100%);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px color-mix(in srgb, var(--secondary-color) 35%, transparent);
      }

      /* Variante Danger */
      .btn-danger {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
      }

      .btn-danger:hover:not(:disabled) {
        background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(239, 68, 68, 0.35);
      }

      /* Variante Ghost */
      .btn-ghost {
        background: transparent;
        color: var(--text-primary);
        box-shadow: none;
      }

      .btn-ghost:hover:not(:disabled) {
        background: var(--bg-secondary);
        transform: translateY(-1px);
      }

      /* Variante Link */
      .btn-link {
        background: transparent;
        color: var(--primary-color);
        padding: 0;
        min-height: auto;
        box-shadow: none;
        text-decoration: none;
      }

      .btn-link:hover:not(:disabled) {
        color: color-mix(in srgb, var(--primary-color) 80%, var(--secondary-color));
        transform: none;
      }

      /* Estados - increased opacity for better WCAG contrast */
      .btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      /* Accessible focus-visible state for keyboard navigation */
      .btn:focus-visible {
        outline: 2px solid var(--primary-color);
        outline-offset: 2px;
      }

      .btn-loading {
        color: transparent;
      }

      /* Spinner de carregamento */
      .btn-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: white;
        border-radius: 50%;
        animation: btn-spin 0.8s linear infinite;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .btn-secondary .btn-spinner,
      .btn-ghost .btn-spinner,
      .btn-link .btn-spinner {
        border-color: rgba(128, 128, 128, 0.3);
        border-top-color: var(--primary-color);
      }

      /* Note: btn-spinner uses custom transform (translate + rotate) so needs local keyframe */
      @keyframes btn-spin {
        to { transform: translate(-50%, -50%) rotate(360deg); }
      }

      /* Ícones */
      .btn svg {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
      }

      .btn-small svg {
        width: 16px;
        height: 16px;
      }

      .btn-large svg {
        width: 20px;
        height: 20px;
      }

      /* Responsivo */
      @media (max-width: 768px) {
        .btn {
          min-height: 48px;
        }
      }
    </style>
  `;
};

// Helper function para criar botões rapidamente
export const createButton = (props) => Button(props);